import {ReactElement, useContext, useEffect, useMemo, useRef, useState} from "react";

import {OpDriverMetricDisplayName} from "../../../../../../constants/Strings";
import {ModelingMethodName} from "../../logic/utils";
import FormulaBarMenu from "../formula-menu/FormulaBarMenu";

import {deleteNodeKeys, TFormulaNodeProps} from "./logic/formulaNode";
import * as css from "./styles/css.module.scss";

import {Button} from "@zendeskgarden/react-buttons";
import {Dropdown, Menu, Trigger} from "@zendeskgarden/react-dropdowns";
import {Field, Input, Label, Message, Toggle} from "@zendeskgarden/react-forms";
import {toast} from "react-toastify";
import {validateOpDriverName} from "../../../../../../components/account-table/logic/DriverHelpers";
import {OperationalMetricTypeIds} from "../../../../../../constants/OperationalMetricTypeIds";
import {TDriverOperationalMetric} from "../../../../../../contexts/account/data/logic/driversAndWorksheetData";
import {AuthContext} from "../../../../../../contexts/AuthContext";
import MinMaxFields, {transformMinMaxValueToDecimalString} from "./components/MinMaxFields";


export type TOperationalFormulaNodeProps = TFormulaNodeProps&{
    driverAccount: {
        metricType: string,
        secondaryType: string | undefined,
        id: string,
        lookbackPeriod: number|null|undefined,
        name: string,
    },
    isBulkUpdateBar?: boolean,
    isPropertyDriversUI?: boolean,
    editableFxBarChecker: ((modelingMethod: ModelingMethodName, modelingMethodLocked: boolean) => boolean) | undefined,
    selectedAccountIds: string[],
    existingDrivers: TDriverOperationalMetric[],
    driver: TDriverOperationalMetric,
    // now that there is a permission to allow managers to change settings, 'locked' is not enough
    // to discriminate what they can do. Being able to delete the driver from the formula bar is the only
    // difference between a manager and an admin when configLocked field is true
    canDelete: boolean,
}

export function OperationalDriverFxNode(props: TOperationalFormulaNodeProps): ReactElement {
    const [isFocused, setIsFocused] = useState<boolean>(false);
    const [innerElementHovered, setInnerElementHovered] = useState<boolean>(false);
    const [nameChangeOpen, setNameChangeOpen] = useState<boolean>(false);
    const [driverName, setDriverName] = useState<string>(props.driver.newName ?? props.driver.name);
    const [newDriverName, setNewDriverName] = useState<string | null>(props.driver.newName ?? props.driver.name);
    const [minValue, setMinValue] = useState<string | null>(props.driver.minValue);
    const [maxValue, setMaxValue] = useState<string | null>(props.driver.maxValue);
    const [isCumSum, setIsCumSum] = useState<boolean>(props.driver.cumSum);
    const [isProrated, setIsProrated] = useState<boolean>(!props.driver.nonProrated);
    const [newDriverNameValid, setNewDriverNameValid] = useState<boolean>(true);
    const [newDriverNameError, setNewDriverNameError] = useState<string>("");
    const [minMaxError, setMinMaxError] = useState<string | null>(null);

    const wrapperRef = useRef<HTMLDivElement>(null);

    const existingDriverNames = useMemo(() => {
        const otherDriversToCheckNameAgainst = props.existingDrivers.filter(x => x.type == props.driver.type && x.name != (props.driver.newName ?? props.driver.name));
        return otherDriversToCheckNameAgainst.map(x => ({name: x.name, type: x.type}));
    }, [props.driver, props.existingDrivers]);

    const authContext = useContext(AuthContext);

    useEffect(() => {
        setDriverName(props.driver.newName ?? props.driver.name);
    }, [props.driver.name]);

    useEffect(() => {
        setIsCumSum(props.driver.cumSum);
    }, [props.driver.cumSum]);

    useEffect(() => {
        setIsProrated(!props.driver.nonProrated);
    }, [props.driver.nonProrated]);

    useEffect(() => {
        setMinValue(props.driver.minValue);
    }, [props.driver.minValue]);

    useEffect(() => {
        setMaxValue(props.driver.maxValue);
    }, [props.driver.maxValue]);

    useEffect(() => {
        if (isFocused && wrapperRef.current) {
            wrapperRef.current.focus();
        }
    }, [isFocused]);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (
                wrapperRef.current &&
                (wrapperRef.current === event.target ||
                wrapperRef.current.contains(event.target as Node))
            ) {
                return;
            }
            setIsFocused(false);
            setNameChangeOpen(false);
        };

        // Clicking outside the current node should remove focus
        document.addEventListener("mousedown", handleClickOutside);

        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, []);

    useEffect(() => {
        if (nameChangeOpen) {
            setIsFocused(false);
        }
    }, [nameChangeOpen]);

    // These are the metrics where a lookback doesn't make sense, so we don't allow it.
    // The calc engine is responsible for ignoring any value in the database, so
    // it doesn't really matter if we accidentally get a value in there.
    const noLookbackAllowed = useMemo(() => {
        return props.driver.sourceMetricId === OperationalMetricTypeIds.EMPLOYEE_COUNT;
    }, [props.driver.sourceMetricId, authContext.operationalMetricTypes]);

    const isEmployeeCountDriver = useMemo(() => {
        return props.driver.sourceMetricId === OperationalMetricTypeIds.EMPLOYEE_COUNT
            || props.driver.secondaryMetricId === OperationalMetricTypeIds.EMPLOYEE_COUNT;
    }, [props.driver.sourceMetricId, props.driver.secondaryMetricId]);

    let lookback = '';

    if(props.driverAccount.lookbackPeriod != undefined){
        if(props.driverAccount.lookbackPeriod < 1){
            lookback = ' from same month';
        } else if(props.driverAccount.lookbackPeriod < 2){
            lookback = ' from 1 month ago';
        } else {
            lookback = ` from ${props.driverAccount.lookbackPeriod} months ago`;
        }
    }

    const handleClick = () => {
        setIsFocused(!isFocused);
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
        if (isFocused && deleteNodeKeys.includes(e.key)) {
            if (props.canDelete) {
                props.fbUpdates.removeOperationalDriver({
                    operationalDriver: props.driver,
                });
            } else {
                toast.error("You do not have permission to remove this driver.");
            }
        }
    };

    const opDriverName = OpDriverMetricDisplayName[props.driverAccount.metricType]?.plural ?? props.driverAccount.metricType;
    let secondaryOpDriverName = "Fee";
    if (props.driverAccount.secondaryType) {
        secondaryOpDriverName = OpDriverMetricDisplayName[props.driverAccount.secondaryType]?.plural ?? props.driverAccount.secondaryType;
    }
    const driverNameRender: string|ReactElement = opDriverName;

    const nodeClassName = props.locked ? css.fxNodeLocked : css.fxNode;

    const className = useMemo(() => {
        const allClassNames = [nodeClassName, css.deletableNode];

        if (props.className) {
            allClassNames.push(props.className);
        }

        if (isFocused) {
            allClassNames.push(css.focusedNode);
        }

        if (innerElementHovered) {
            allClassNames.push(css.disableHover);
        } else {
            allClassNames.filter((each: string) => each !== css.disableHover);
        }

        return allClassNames.join(' ');
    }, [isFocused, innerElementHovered, nodeClassName]);

    const lookbackNode = <a>{lookback}</a>;

    const renderLookbackElement = (): string | JSX.Element => {
        if (lookback.length > 0) {
            // Check if the formula bar is locked
            if (props.locked || noLookbackAllowed) {
                return lookback;
            } else {
                return (
                    <FormulaBarMenu
                        triggerNode={lookbackNode}
                        lookbackRefObject={{driver: "operational", refObject: props.driverAccount, operationalDriver: props.driver}}
                        fbUpdates={props.fbUpdates}
                        isBulkUpdateBar={props.isBulkUpdateBar}
                        isPropertyDriversUI={props.isPropertyDriversUI}
                        editableFxBarChecker={props.editableFxBarChecker}
                        selectedAccountIds={props.selectedAccountIds}
                    />
                );
            }
        }
        return <></>;
    };

    const renderRampupElement = (): string | JSX.Element => {
        return (
            <div className={css.opNodeMenuWrapper}>
                <Dropdown>
                    <Trigger>
                        <Button isBasic className={css.opNodeMenuTrigger} disabled={props.locked} onClick={(evt) => evt.stopPropagation()}>{ isCumSum ? "with ramp-up" : "no ramp-up"}</Button>
                    </Trigger>
                    <Menu placement="bottom" className={css.opNodeMenu} onClick={(evt) => evt.stopPropagation()}>
                        <Field>
                            <Toggle
                                isCompact
                                checked={isCumSum}
                                disabled={props.locked}
                                onChange={(evt) => {
                                    props.fbUpdates.updateOperationalCumSum({
                                        operationalDriver: props.driver,
                                        cumSum: evt.target.checked,
                                    });
                                }}
                            >
                                <Label>Ramp-up</Label>
                                <Message>This will make each month carry over the previous month's total.</Message>
                            </Toggle>
                        </Field>
                    </Menu>
                </Dropdown>
            </div>
        );
    };

    const renderProratedElement = (): string | JSX.Element => {
        if (!isEmployeeCountDriver) {
            return <></>;
        }
        return (
            <div className={css.opNodeMenuWrapper}>
                <Dropdown>
                    <Trigger>
                        <Button isBasic className={css.opNodeMenuTrigger} disabled={props.locked} onClick={(evt) => evt.stopPropagation()}>{ isProrated ? "prorated" : "full count"}</Button>
                    </Trigger>
                    <Menu placement="bottom" className={css.opNodeMenu} onClick={(evt) => evt.stopPropagation()}>
                        <Field>
                            <Toggle
                                isCompact
                                checked={isProrated}
                                disabled={props.locked}
                                onChange={(evt) => {
                                    props.fbUpdates.updateOperationalNonProrated({
                                        operationalDriver: props.driver,
                                        nonProrated: !evt.target.checked,
                                    });
                                }}
                            >
                                <Label>{isProrated ? "Prorated" : "Full Count"}</Label>
                                <Message>{isProrated
                                    ? "Prorates the employee count based on their start date."
                                    : "Counts each employee in full for every month."}
                                </Message>
                            </Toggle>
                        </Field>
                    </Menu>
                </Dropdown>
            </div>
        );
    };

    const renderMinMaxElement = (): string | JSX.Element => {
        return (
            <div className={css.opNodeMenuWrapper}>
                <Dropdown
                    onStateChange={options => {
                        if (!options.isOpen) {
                            // if the user leaves the form in an error state, then reset the values back to what they were on initial load
                            if (minMaxError !== null) {
                                setMinValue(props.driver.minValue);
                                setMaxValue(props.driver.maxValue);
                                return;
                            }

                            props.fbUpdates.updateOperationalMinMax({
                                operationalDriver: props.driver,
                                minValue: transformMinMaxValueToDecimalString(minValue),
                                maxValue: transformMinMaxValueToDecimalString(maxValue),
                            });
                        }
                    }}
                >
                    <Trigger>
                        <Button
                            isBasic
                            className={`${css.opNodeMenuTrigger} ${minMaxError !== null && css.isError}`}
                            disabled={props.locked}
                            onClick={(evt) => evt.stopPropagation()}
                        >
                            Min: {minValue === null || minValue.trim() === "" ? "None" : minValue}, Max: {maxValue === null || maxValue.trim() === "" ? "None" : maxValue}
                        </Button>
                    </Trigger>
                    <Menu placement="bottom" className={css.opNodeMenu} onClick={(evt) => evt.stopPropagation()}>
                        <MinMaxFields
                            minValue={minValue}
                            maxValue={maxValue}
                            onChange={vals => {
                                setMinValue(vals.minValue);
                                setMaxValue(vals.maxValue);
                            }}
                            onErrorUpdate={setMinMaxError}
                        />
                    </Menu>
                </Dropdown>
            </div>
        );
    };

    const renderNameElement = (): string | JSX.Element => {
        return (
            <div className={css.opNodeMenuWrapper}>
                <Dropdown isOpen={nameChangeOpen}>
                    <Trigger>
                        <Button isBasic className={css.opNodeMenuTrigger} onClick={(evt) => {
                            setNameChangeOpen(!nameChangeOpen);
                            evt.stopPropagation();
                        }}>{ driverName }</Button>
                    </Trigger>
                    <Menu
                        placement="bottom"
                        className={css.opNodeMenu}
                        onClick={(evt) => {
                            evt.stopPropagation();
                        }}
                    >
                        <Field>
                            <Label>Driver Name</Label>
                            <Input
                                defaultValue={driverName}
                                onChange={(evt) => {
                                    const validatedName = validateOpDriverName({name: evt.target.value, type: props.driver.type}, existingDriverNames);
                                    setNewDriverNameValid(validatedName.isValid);
                                    setNewDriverNameError(validatedName.error);
                                    setNewDriverName(evt.target.value.trim());
                                }}
                                onKeyDown={(evt) => {
                                    if (evt.key === "Enter") {
                                        saveNewDriverName();
                                    }
                                }}
                            />
                            { !newDriverNameValid &&
                                <Message validation="error">{newDriverNameError}</Message>
                            }
                        </Field>
                        <div className={css.opNodeActions}>
                            <Button isDanger isBasic size="small" onClick={() => setNameChangeOpen(false)}>Cancel</Button>
                            <Button isPrimary size="small" disabled={!newDriverNameValid || props.locked} onClick={saveNewDriverName}>Save</Button>
                        </div>
                    </Menu>
                </Dropdown>
            </div>
        );
    };

    function saveNewDriverName() {
        if (newDriverName == driverName || newDriverName == null || props.locked) {
            setNameChangeOpen(false);
            return;
        }

        const validatedName = validateOpDriverName({name: newDriverName, type: props.driver.type}, existingDriverNames);

        if (validatedName.isValid) {
            props.fbUpdates.updateOperationalName({
                operationalDriver: props.driver,
                newName: newDriverName,
            });
            setDriverName(newDriverName);
            setNameChangeOpen(false);
        } else {
            toast.error(validatedName.error);
        }
    }

    return (
        <div
            className={className}
            onClick={handleClick}
            onKeyDown={handleKeyDown}
            /**
             * onMouseLeave() isn't always triggered when the user selects a lookback period
             * and the lookback menu closes, so we make sure by doing this:
            */
            onMouseEnter={() => setInnerElementHovered(false)}
            ref={wrapperRef}
            tabIndex={0}
        >
            (
            <div
                className={nodeClassName}
                onMouseEnter={() => setInnerElementHovered(true)}
                onMouseLeave={() => setInnerElementHovered(false)}
            >
                {renderNameElement()}
            </div>
            : {secondaryOpDriverName} * % of {driverNameRender}&nbsp;
            <div
                className={css.fxNode}
                onMouseEnter={() => setInnerElementHovered(true)}
                onMouseLeave={() => setInnerElementHovered(false)}
            >
                {renderLookbackElement()}, {renderRampupElement()}, {renderProratedElement()}, {renderMinMaxElement()}
            </div>)
        </div>
    );
}
